import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { Send, MessageSquare, Bug, Lightbulb, Loader2, Check, MapPin, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { id: "bug_report", label: "Bug Report", icon: Bug, color: "text-red-400" },
  { id: "feature_request", label: "Feature Request", icon: Lightbulb, color: "text-amber-400" },
  { id: "general", label: "General Feedback", icon: MessageSquare, color: "text-emerald-400" },
];

export default function FeedbackPage() {
  const { getAuthHeader } = useAuth();
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  
  // Additional bug report fields
  const [bugLocation, setBugLocation] = useState("");
  const [bugSteps, setBugSteps] = useState("");
  const [bugReproducible, setBugReproducible] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (message.trim().length < 10) {
      setError("Please write at least 10 characters");
      return;
    }
    
    setError("");
    setSubmitting(true);
    
    try {
      // Combine all fields for bug reports
      let fullMessage = message.trim();
      if (category === "bug_report") {
        const bugDetails = [];
        if (bugLocation) bugDetails.push(`**Where did you see the bug?** ${bugLocation}`);
        if (bugSteps) bugDetails.push(`**Steps to reproduce:** ${bugSteps}`);
        if (bugReproducible) bugDetails.push(`**Can you repeat the bug?** ${bugReproducible}`);
        if (bugDetails.length > 0) {
          fullMessage = `${fullMessage}\n\n---\n${bugDetails.join('\n\n')}`;
        }
      }
      
      await axios.post(`${API}/feedback/simple`, {
        category,
        message: fullMessage
      }, { headers: getAuthHeader() });
      
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-md mx-auto text-center py-12" data-testid="feedback-success">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="text-emerald-400" size={32} />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Thank You!</h2>
        <p className="text-slate-400 mb-6">
          Your feedback has been submitted. We appreciate you taking the time to help us improve.
        </p>
        <button
          onClick={() => {
            setSubmitted(false);
            setMessage("");
            setCategory("general");
            setBugLocation("");
            setBugSteps("");
            setBugReproducible("");
          }}
          className="text-emerald-400 hover:underline"
        >
          Submit More Feedback
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pb-8" data-testid="feedback-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Feedback</h1>
        <p className="text-slate-400 text-sm">Help us improve APL</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Category Selection */}
        <div>
          <label className="block text-sm text-slate-400 mb-2">Category</label>
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                    category === cat.id
                      ? "bg-slate-700 border-emerald-500"
                      : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  <Icon className={cat.color} size={20} />
                  <span className="text-xs text-slate-300">{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bug-specific questions */}
        {category === "bug_report" && (
          <div className="space-y-3 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
            <p className="text-red-400 text-xs font-medium mb-2">Help us find and fix this bug faster:</p>
            
            <div>
              <label className="flex items-center gap-1 text-sm text-slate-400 mb-1">
                <MapPin size={14} /> Where did you see the bug?
              </label>
              <input
                type="text"
                value={bugLocation}
                onChange={(e) => setBugLocation(e.target.value)}
                placeholder="e.g., Match Day page, Team Builder, Predictor..."
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-red-500 text-sm"
                data-testid="bug-location"
              />
            </div>
            
            <div>
              <label className="flex items-center gap-1 text-sm text-slate-400 mb-1">
                <RefreshCw size={14} /> Can you reproduce the bug?
              </label>
              <div className="flex gap-2">
                {["Yes, every time", "Sometimes", "Only once"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setBugReproducible(option)}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs transition-all ${
                      bugReproducible === option
                        ? "bg-red-500/20 border border-red-500 text-red-400"
                        : "bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid={`bug-reproducible-${option}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-1 block">
                Steps to reproduce (optional)
              </label>
              <textarea
                value={bugSteps}
                onChange={(e) => setBugSteps(e.target.value)}
                placeholder="1. Go to...\n2. Click on...\n3. See the error..."
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-red-500 text-sm resize-none h-20"
                data-testid="bug-steps"
              />
            </div>
          </div>
        )}

        {/* Message */}
        <div>
          <label className="block text-sm text-slate-400 mb-2">
            {category === "bug_report" ? "Describe the bug" : category === "feature_request" ? "What feature would you like?" : "Your Message"} 
            <span className="text-slate-600 ml-1">({message.length}/1000)</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
            placeholder={
              category === "bug_report"
                ? "What went wrong? What did you expect to happen?"
                : category === "feature_request"
                ? "What feature would you like to see?"
                : "Share your thoughts with us..."
            }
            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 resize-none h-32"
            data-testid="feedback-message"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting || message.trim().length < 10}
          className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="submit-feedback"
        >
          {submitting ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <Send size={20} />
          )}
          {submitting ? "Sending..." : "Submit Feedback"}
        </button>
      </form>

      <p className="text-slate-500 text-xs text-center mt-4">
        Your feedback helps us make APL better for everyone.
      </p>
    </div>
  );
}
